import React, { useState, useEffect, useRef, useCallback } from 'react'
import { FaTrash } from 'react-icons/fa'
import {
  Btn,
  CustomDropdown,
  CustomDropdownWithFilter,
  CustomModal,
  CustomCalendar,
  InputNumeroInteiro,
  InputNumeroDecimal,
  InputText,
  CustomInputTextArea,
  SectionTitle,
} from '../../../../../../components'
import { getEmissoresByTps } from '../../../../../../services/api/https/settings/emissor-cte.http'
import { postEmitCteSemNota } from '../../../../../../services/api/https/deliveries/cte.http'
import { getCfops } from '../../../../../../services/api/https/settings/cfop.http'
import { getCities, getUfs } from '../../../../../../services/api/https/system/system.http'
import {
  CTE_TIPOS_OUTROS_DOCUMENTOS,
  CTE_TOMADORES,
  CTE_UFS_SEM_TOMADOR_ISENTO,
  CTE_TIPOS_MEDIDA,
  CTE_UNIDADES_CARGA,
} from '../../../../../../constants/cte-sem-nota'
import { normalizarCnpj, somenteDigitos } from '../../../../../../services/util/documento.service'
import ValoresFiscaisCte from '../../ValoresFiscaisCte/ValoresFiscaisCte'
import {
  ERROS_VALORES_FISCAIS,
  VALORES_FISCAIS_INICIAIS,
  erroNumeroOrdemColeta,
  erroValoresFiscais,
  montarComponentesValor,
  round2,
  toNum,
} from '../../ValoresFiscaisCte/valoresFiscaisCte.util'
import CteParteForm from './CteParteForm'
import RadioGroup from './RadioGroup'
import { dataParaIso, parteVazia, validarParte } from './cteSemNota.util'

const inicialState = {
  emissor_id: null,
  cfop_id: null,
  numero_ordem_coleta: null,
  ...VALORES_FISCAIS_INICIAIS,
  observacoes: null,
}

const errors = {
  emissor_id: 'O campo Emissor é obrigatório',
  cfop_id: 'O campo CFOP é obrigatório',
  numero_ordem_coleta: 'O campo Nº Ordem Coleta é obrigatório',
  ...ERROS_VALORES_FISCAIS,
}

const cargaInicial = () => ({
  valor_carga: null,
  produto_predominante: '',
  quantidade: null,
  unidade: CTE_UNIDADES_CARGA[0],
  tipo_medida: CTE_UNIDADES_CARGA[0].tipoMedidaPadrao,
})

const prestacaoInicial = () => ({
  inicio: { uf: null, city: null, tocado: false },
  fim: { uf: null, city: null, tocado: false },
})

export default function ModalEmitirCteSemNota({
  selectedTp,
  visible,
  onModalClose,
  onModalSave,
  onError,
}) {
  // Hooks
  const [isLoading, setIsLoading] = useState(false)
  const [state, setState] = useState(inicialState)
  const [componentes, setComponentes] = useState([])
  const proximaChaveDocumento = useRef(1)
  const [emissores, setEmissores] = useState({ emissores: [], selected: null })
  const [cfops, setCfops] = useState({ cfops: [], selected: null })

  // Partes, prestação, carga e documentos (exclusivos do CTe sem Nota Fiscal)
  const [ufs, setUfs] = useState([])
  const [remetente, setRemetente] = useState(parteVazia('empresa'))
  const [destinatario, setDestinatario] = useState(parteVazia('manual'))
  const [tomador, setTomador] = useState('0')
  const [prestacao, setPrestacao] = useState(prestacaoInicial())
  const [cidadesPrestacao, setCidadesPrestacao] = useState({ inicio: [], fim: [] })
  const [carga, setCarga] = useState(cargaInicial())
  const [documentos, setDocumentos] = useState([])
  const cacheCidades = useRef(new Map())

  const novoDocumento = () => ({
    key: proximaChaveDocumento.current++,
    tipo_documento: '99',
    descricao: '',
    numero: '',
    data_emissao: null,
    valor: null,
  })

  const carregarCidades = useCallback(async ufId => {
    if (!ufId) return []
    if (cacheCidades.current.has(ufId)) return cacheCidades.current.get(ufId)
    const lista = await getCities(ufId)
    cacheCidades.current.set(ufId, lista)
    return lista
  }, [])

  // Reinicia o formulário a cada abertura (uma linha de documento tipo 99 por padrão)
  useEffect(() => {
    if (!visible) return
    setState(inicialState)
    setComponentes([])
    setEmissores(prevState => ({ ...prevState, selected: null }))
    setCfops(prevState => ({ ...prevState, selected: null }))
    setRemetente(parteVazia('empresa'))
    setDestinatario(parteVazia('manual'))
    setTomador('0')
    setPrestacao(prestacaoInicial())
    setCarga(cargaInicial())
    setDocumentos([novoDocumento()])
  }, [visible])

  useEffect(() => {
    async function fetchData() {
      try {
        const [listaEmissores, listaCfops, listaUfs] = await Promise.all([
          getEmissoresByTps(selectedTp),
          getCfops(),
          getUfs(),
        ])
        setEmissores({
          emissores: listaEmissores,
          selected: null,
        })
        setCfops({
          cfops: listaCfops.map(cfop => ({
            ...cfop,
            codigoNatureza: `${cfop.codigo} - ${cfop.natureza}`,
          })),
          selected: null,
        })
        setUfs(listaUfs)
      } catch (error) {
        onError(error)
      }
    }
    if (selectedTp) fetchData()
  }, [selectedTp])

  // Início e fim da prestação acompanham remetente/destinatário até o usuário mexer neles
  useEffect(() => {
    setPrestacao(prevState =>
      prevState.inicio.tocado
        ? prevState
        : { ...prevState, inicio: { ...prevState.inicio, uf: remetente.uf, city: remetente.city } },
    )
  }, [remetente.uf, remetente.city])

  useEffect(() => {
    setPrestacao(prevState =>
      prevState.fim.tocado
        ? prevState
        : { ...prevState, fim: { ...prevState.fim, uf: destinatario.uf, city: destinatario.city } },
    )
  }, [destinatario.uf, destinatario.city])

  const ufInicioId = prestacao.inicio.uf ? prestacao.inicio.uf.id : null
  const ufFimId = prestacao.fim.uf ? prestacao.fim.uf.id : null

  useEffect(() => {
    let ativo = true
    if (!ufInicioId) {
      setCidadesPrestacao(prevState => ({ ...prevState, inicio: [] }))
      return undefined
    }
    carregarCidades(ufInicioId)
      .then(lista => ativo && setCidadesPrestacao(prevState => ({ ...prevState, inicio: lista })))
      .catch(error => ativo && onError(error))
    return () => {
      ativo = false
    }
  }, [ufInicioId])

  useEffect(() => {
    let ativo = true
    if (!ufFimId) {
      setCidadesPrestacao(prevState => ({ ...prevState, fim: [] }))
      return undefined
    }
    carregarCidades(ufFimId)
      .then(lista => ativo && setCidadesPrestacao(prevState => ({ ...prevState, fim: lista })))
      .catch(error => ativo && onError(error))
    return () => {
      ativo = false
    }
  }, [ufFimId])

  const ufInicio = prestacao.inicio.uf ? prestacao.inicio.uf.uf : null
  const ufFim = prestacao.fim.uf ? prestacao.fim.uf.uf : null

  const mesmaUf = Boolean(ufInicio && ufFim && ufInicio === ufFim)
  const tiposDocumento = CTE_TIPOS_OUTROS_DOCUMENTOS.filter(
    tipo => !tipo.somenteIntraestadual || mesmaUf,
  )

  // Helpers - prestação, carga e documentos
  const onPrestacaoChange = (ponto, patch) => {
    setPrestacao(prevState => ({
      ...prevState,
      [ponto]: { ...prevState[ponto], ...patch, tocado: true },
    }))
  }

  // A unidade sugere o tipo de medida (KG/TON: PESO BRUTO, UN: UNIDADES, M3: CUBAGEM, L: LITRAGEM)
  const onUnidadeChange = unidade => {
    setCarga(prevState => ({ ...prevState, unidade, tipo_medida: unidade.tipoMedidaPadrao }))
  }

  const onDocumentoChange = (item, campo, valor) => {
    setDocumentos(prevState =>
      prevState.map(doc => (doc.key === item.key ? { ...doc, [campo]: valor } : doc)),
    )
  }

  const onAdicionarDocumento = () => setDocumentos(prevState => [...prevState, novoDocumento()])

  const onRemoverDocumento = item => {
    setDocumentos(prevState =>
      prevState.length === 1 ? prevState : prevState.filter(doc => doc.key !== item.key),
    )
  }

  // Validação (espelho das regras do backend; a primeira inconsistência é devolvida)
  const validar = () => {
    if (!selectedTp) return 'Selecione o Operador Logístico'
    for (const key of Object.keys(state)) {
      if (key !== 'observacoes' && state[key] === null) return errors[key]
    }
    const erroOrdemColeta = erroNumeroOrdemColeta(state.numero_ordem_coleta)
    if (erroOrdemColeta) return erroOrdemColeta
    const erroRemetente = validarParte(remetente, 'Remetente')
    if (erroRemetente) return erroRemetente
    const erroDestinatario = validarParte(destinatario, 'Destinatário')
    if (erroDestinatario) return erroDestinatario

    const parteTomadora = tomador === '0' ? remetente : destinatario
    if (
      parteTomadora.situacao_icms === 'isento' &&
      parteTomadora.uf &&
      CTE_UFS_SEM_TOMADOR_ISENTO.includes(parteTomadora.uf.uf)
    ) {
      return `A UF ${parteTomadora.uf.uf} não aceita tomador isento de IE: use Contribuinte com IE ou Não contribuinte`
    }
    if (!prestacao.inicio.uf || !prestacao.inicio.city)
      return 'Selecione a UF e a cidade de início da prestação'
    if (!prestacao.fim.uf || !prestacao.fim.city)
      return 'Selecione a UF e a cidade de fim da prestação'

    if (!cfops.selected) return 'Selecione o CFOP'

    if (!(toNum(carga.valor_carga) > 0)) return 'Informe o Valor da Carga'
    const produto = String(carga.produto_predominante || '').trim()
    if (!produto || produto.length > 60) return 'Informe o Produto Predominante (até 60 caracteres)'
    if (!(toNum(carga.quantidade) > 0)) return 'Informe a Quantidade da Carga'
    const tipoMedida = String(carga.tipo_medida || '').trim()
    if (!tipoMedida || tipoMedida.length > 20)
      return 'Informe o Tipo de Medida da carga (até 20 caracteres)'

    if (!documentos.length) return 'Informe ao menos um documento'
    for (let i = 0; i < documentos.length; i++) {
      const doc = documentos[i]
      const posicao = `Documento ${i + 1}`
      if (!doc.tipo_documento) return `${posicao}: selecione o tipo`
      if (!String(doc.numero || '').trim()) return `${posicao}: informe o número`
      if (doc.tipo_documento === '99' && !String(doc.descricao || '').trim()) {
        return `${posicao}: a descrição é obrigatória para o tipo Outros`
      }
      if (['59', '65'].includes(doc.tipo_documento) && !mesmaUf) {
        return `${posicao}: CF-e SAT e NFC-e só são aceitos em prestação dentro da mesma UF`
      }
      if (doc.data_emissao && !dataParaIso(doc.data_emissao)) {
        return `${posicao}: data de emissão inválida`
      }
    }

    const erroFiscal = erroValoresFiscais(state)
    if (erroFiscal) return erroFiscal
    if (state.observacoes && state.observacoes.length > 2000) {
      return 'O campo Observações deve ter no máximo 2000 caracteres'
    }
    return null
  }

  const montarParte = parte => ({
    origem: parte.origem,
    company_id: parte.company_id,
    client_id: parte.client_id,
    tipo_pessoa: parte.tipo_pessoa,
    cnpj_cpf:
      parte.tipo_pessoa === 'PF' ? somenteDigitos(parte.cnpj_cpf) : normalizarCnpj(parte.cnpj_cpf),
    situacao_icms: parte.situacao_icms,
    inscricao_estadual:
      parte.situacao_icms === 'contribuinte' ? somenteDigitos(parte.inscricao_estadual) : null,
    nome: String(parte.nome).trim().slice(0, 60),
    telefone: somenteDigitos(parte.telefone) || null,
    logradouro: String(parte.logradouro).trim(),
    numero: String(parte.numero).trim(),
    bairro: String(parte.bairro).trim(),
    city_id: parte.city.id,
    cep: somenteDigitos(parte.cep),
  })

  // Events
  const onBtnSalvarClicked = async () => {
    try {
      const erro = validar()
      if (erro) throw new Error(erro)

      setIsLoading(true)

      const componentesValor = montarComponentesValor(componentes)

      await postEmitCteSemNota(selectedTp, {
        ...state,
        tomador,
        remetente: montarParte(remetente),
        destinatario: montarParte(destinatario),
        city_inicio_id: prestacao.inicio.city.id,
        city_fim_id: prestacao.fim.city.id,
        carga: {
          valor_carga: round2(toNum(carga.valor_carga)),
          produto_predominante: String(carga.produto_predominante).trim(),
          quantidade: toNum(carga.quantidade),
          unidade: carga.unidade.value,
          tipo_medida: String(carga.tipo_medida).trim(),
        },
        outros_documentos: documentos.map(doc => ({
          tipo_documento: doc.tipo_documento,
          descricao: String(doc.descricao || '').trim() || null,
          numero: String(doc.numero).trim(),
          data_emissao: dataParaIso(doc.data_emissao),
          valor: toNum(doc.valor),
        })),
        ...(componentesValor.length ? { componentes_valor: componentesValor } : {}),
      })

      onModalSave()
    } catch (error) {
      onError(error)
    } finally {
      setIsLoading(false)
    }
  }

  // UF em coluna de 3/12: o dropdown do PrimeReact tem largura mínima e transborda em colunas menores
  const renderPrestacao = (ponto, titulo) => (
    <>
      <div className="p-md-3 p-col-12">
        <CustomDropdown
          optionLabel="uf"
          options={ufs}
          value={prestacao[ponto].uf}
          label={`UF ${titulo}:*`}
          filter={true}
          onChange={uf => onPrestacaoChange(ponto, { uf, city: null })}
        />
      </div>
      <div className="p-md-9 p-col-12">
        <CustomDropdownWithFilter
          optionLabel="name"
          options={cidadesPrestacao[ponto]}
          value={prestacao[ponto].city}
          label={`Cidade ${titulo}:*`}
          filter={true}
          disabled={!prestacao[ponto].uf}
          onChange={city => onPrestacaoChange(ponto, { city })}
        />
      </div>
    </>
  )

  // Render
  return (
    <CustomModal
      isVisible={visible}
      header={'Emitir CTe sem Nota Fiscal'}
      width={900}
      onClose={onModalClose}
      contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
    >
      <form className="p-grid" onSubmit={e => e.preventDefault()}>
        <div className="p-col-12">
          <div
            style={{
              backgroundColor: '#e3f2fd',
              padding: '10px 15px',
              borderRadius: '5px',
              marginBottom: '10px',
            }}
          >
            CTe emitido sem vínculo com Notas Fiscais: os documentos da carga vão como "Outros"
            (Declaração / Outros) e os dados de remetente, destinatário, prestação e carga são
            informados aqui.
          </div>
        </div>

        <div className="p-md-6 p-col-12">
          <CustomDropdown
            optionLabel="razao_social"
            options={emissores.emissores}
            value={emissores.selected}
            label="Emissor:*"
            filter={true}
            onChange={value => {
              setState(prevState => ({ ...prevState, emissor_id: value.id }))
              setEmissores(prevState => ({ ...prevState, selected: value }))
            }}
          />
        </div>
        <div className="p-md-6 p-col-12">
          <InputNumeroInteiro
            label="Nº Ordem Coleta:*"
            marginTop={10}
            value={state.numero_ordem_coleta}
            onChange={value => setState({ ...state, numero_ordem_coleta: value })}
          />
          <span style={{ color: 'gray', fontSize: 12 }}>
            Número gerado pela transportadora associado a ordem de coleta
          </span>
        </div>

        <CteParteForm
          papel="remetente"
          titulo="Remetente"
          parte={remetente}
          onChange={setRemetente}
          ufs={ufs}
          selectedTp={selectedTp}
          carregarCidades={carregarCidades}
          onError={onError}
        />
        <CteParteForm
          papel="destinatario"
          titulo="Destinatário"
          parte={destinatario}
          onChange={setDestinatario}
          ufs={ufs}
          selectedTp={selectedTp}
          carregarCidades={carregarCidades}
          onError={onError}
        />

        {/* Tomador */}
        <div className="p-col-12">
          <SectionTitle title="Tomador do Serviço" />
          <div style={{ marginTop: 5 }}>
            <RadioGroup
              nome="tomador"
              items={CTE_TOMADORES}
              value={tomador}
              onChange={setTomador}
            />
          </div>
          <span style={{ color: 'gray', fontSize: 12 }}>
            O indicador de IE do tomador é derivado da situação no ICMS da parte escolhida.
          </span>
        </div>

        {/* Prestação */}
        <div className="p-col-12">
          <SectionTitle title="Prestação" />
          <span style={{ color: 'gray', fontSize: 12 }}>
            Início e fim vêm preenchidos com as cidades do remetente e do destinatário; ajuste se
            necessário. Início e fim no mesmo município indicam serviço sujeito a ISS, não a ICMS.
          </span>
        </div>
        {renderPrestacao('inicio', 'Início')}
        {renderPrestacao('fim', 'Fim')}
        <div className="p-col-12">
          <CustomDropdown
            optionLabel="codigoNatureza"
            options={cfops.cfops}
            value={cfops.selected}
            label="CFOP:*"
            filter={true}
            onChange={value => {
              setState(prevState => ({ ...prevState, cfop_id: value.id }))
              setCfops(prevState => ({ ...prevState, selected: value }))
            }}
            tooltip={'Lista filtrada pelas UFs do emissor, de início e de fim da prestação'}
          />
        </div>

        {/* Carga */}
        <div className="p-col-12">
          <SectionTitle title="Carga" />
        </div>
        <div className="p-md-3 p-col-12">
          <InputNumeroDecimal
            label="Valor da Carga:*"
            marginTop={10}
            placeholder="Ex.: 125.000,00"
            value={carga.valor_carga}
            onChange={value => setCarga({ ...carga, valor_carga: value })}
          />
        </div>
        <div className="p-md-5 p-col-12">
          <InputText
            label="Produto Predominante:*"
            marginTop={10}
            value={carga.produto_predominante}
            maxLength={60}
            placeholder="Ex.: PRODUTOS LACTEOS, ALIMENTOS REFRIGERADOS, BEBIDAS"
            onChange={value => setCarga({ ...carga, produto_predominante: value })}
          />
        </div>
        <div className="p-md-4 p-col-12">
          <CustomDropdown
            optionLabel="label"
            options={CTE_UNIDADES_CARGA}
            value={carga.unidade}
            label="Unidade de medida:*"
            onChange={onUnidadeChange}
          />
        </div>
        <div className="p-md-3 p-col-12">
          <InputNumeroDecimal
            label="Quantidade:*"
            marginTop={10}
            decimalScale={4}
            placeholder="Ex.: 18.500,50"
            value={carga.quantidade}
            onChange={value => setCarga({ ...carga, quantidade: value })}
          />
        </div>
        <div className="p-md-5 p-col-12">
          <CustomDropdown
            optionLabel="label"
            options={CTE_TIPOS_MEDIDA}
            value={CTE_TIPOS_MEDIDA.find(tipo => tipo.value === carga.tipo_medida) || null}
            label="Tipo de medida:*"
            onChange={tipo => setCarga({ ...carga, tipo_medida: tipo.value })}
            tooltip="Sugerido pela unidade; exemplos do leiaute da SEFAZ"
          />
        </div>

        {/* Documentos (infOutros) */}
        <div className="p-col-12" style={{ marginTop: 10 }}>
          <SectionTitle title="Documentos da Carga" />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 5,
            }}
          >
            <span style={{ color: 'gray', fontSize: 12 }}>
              A SEFAZ exige ao menos um documento. Descrição obrigatória no tipo Outros; CF-e SAT e
              NFC-e só em prestação dentro da mesma UF.
            </span>
            <Btn onClick={onAdicionarDocumento}>Adicionar documento</Btn>
          </div>
        </div>
        {documentos.map((doc, index) => (
          <React.Fragment key={doc.key}>
            <div className="p-md-3 p-col-12">
              <CustomDropdown
                optionLabel="label"
                options={tiposDocumento}
                value={tiposDocumento.find(tipo => tipo.value === doc.tipo_documento) || null}
                label={`Tipo do documento ${index + 1}:*`}
                onChange={tipo => onDocumentoChange(doc, 'tipo_documento', tipo.value)}
              />
            </div>
            <div className="p-md-4 p-col-12">
              <InputText
                label={doc.tipo_documento === '99' ? 'Descrição:*' : 'Descrição:'}
                marginTop={10}
                value={doc.descricao}
                maxLength={100}
                placeholder="Ex.: Apuracao de frete"
                onChange={value => onDocumentoChange(doc, 'descricao', value)}
              />
            </div>
            <div className="p-md-2 p-col-12">
              <InputText
                label="Número:*"
                marginTop={10}
                value={doc.numero}
                maxLength={20}
                placeholder="Ex.: AP-2030-01"
                onChange={value => onDocumentoChange(doc, 'numero', value)}
              />
            </div>
            <div className="p-md-2 p-col-12">
              <CustomCalendar
                label="Data emissão:"
                marginTop={10}
                value={doc.data_emissao}
                maxDate={new Date()}
                appendTo={document.body}
                onChange={value => onDocumentoChange(doc, 'data_emissao', value)}
              />
            </div>
            <div
              className="p-md-1 p-col-12"
              style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 15 }}
            >
              {documentos.length > 1 && (
                <FaTrash
                  style={{ cursor: 'pointer' }}
                  color="red"
                  onClick={() => onRemoverDocumento(doc)}
                />
              )}
            </div>
            <div className="p-md-3 p-col-12">
              <InputNumeroDecimal
                label="Valor do documento:"
                marginTop={0}
                placeholder="Ex.: 125.000,00"
                value={doc.valor}
                onChange={value => onDocumentoChange(doc, 'valor', value)}
              />
            </div>
            <div className="p-md-9 p-col-12" />
          </React.Fragment>
        ))}

        <ValoresFiscaisCte
          key={visible ? 'aberto' : 'fechado'}
          state={state}
          onStateChange={setState}
          componentes={componentes}
          onComponentesChange={setComponentes}
          onError={onError}
        />

        <div className="p-col-12">
          <CustomInputTextArea
            label="Observações"
            marginTop={10}
            rows={4}
            value={state.observacoes || ''}
            onChange={value => setState({ ...state, observacoes: value })}
          />
        </div>

        <div className="p-col-12" style={{ display: 'flex', justifyContent: 'end' }}>
          <Btn onClick={onBtnSalvarClicked} isLoading={isLoading}>
            Salvar
          </Btn>
        </div>
      </form>
    </CustomModal>
  )
}
